import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import Heading from "../../components/common/Typography/Heading";
import PricingSlider from "../BusinessDashboard/Subscriptions/PricingPlans/PricingSlider";
import PriceCard from "../../components/common/PriceCard/PriceCard";
import PackageDetailsTable from "./PackageDetailsTable";
import ButtonText from "../../components/common/Typography/ButtonText";
import { getPricingPlan } from "../../data/pricingPlans";
import {
  publicMainHeaderOffsetClass,
  PUBLIC_HERO_TO_SECTION_CLASS,
  PUBLIC_HEADING_TO_CONTENT_CLASS,
  PUBLIC_CONTENT_TO_CTA_CLASS,
} from "../Public/shared/publicLayoutSpec";

const HeroImage = () => {
  const [showPackageDetails, setShowPackageDetails] = useState(false);
  const tableRef = useRef(null);
  
  // Get pricing plan data
  const basicPlan = getPricingPlan('basic');
  const proPlan = getPricingPlan('pro');

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const slowScrollTo = (element) => {
    const start = window.pageYOffset;
    const target = element.getBoundingClientRect().top + window.pageYOffset - 0; // 100px offset from top
    const distance = target - start;
    const duration = 1000; // 1.5 seconds for slow scroll
    let startTime = null;

    const animation = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeInOutCubic = progress => progress < 0.5 
        ? 4 * progress * progress * progress 
        : (progress - 1) * (2 * progress - 2) * (2 * progress - 2) + 1;
      
      window.scrollTo(0, start + distance * easeInOutCubic(progress));
      
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    };
    
    requestAnimationFrame(animation);
  };

  const togglePackageDetails = () => {
    setShowPackageDetails(!showPackageDetails);
    
    // Scroll to table when opening
    if (!showPackageDetails && tableRef.current) {
      setTimeout(() => {
        slowScrollTo(tableRef.current);
      }, 100); // Small delay to let the animation start
    }
  };

  return (
    <>
      <div className={PUBLIC_HERO_TO_SECTION_CLASS}>
        <div className={`mx-auto ${publicMainHeaderOffsetClass()}`}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Heading
              variant={"h1"}
              className={"text-center font-extrabold"}
            >
              Choose Your Plan
            </Heading>
          </motion.div>

          <div className={PUBLIC_HEADING_TO_CONTENT_CLASS}>
            <div className="tp-container-small mx-auto w-full">
            {/* Mobile Pricing Slider */}
            <motion.div
              className="flex justify-center lg:hidden w-full"
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <PricingSlider />
            </motion.div>

            {/* Desktop Pricing Cards */}
            <motion.div
              className="hidden lg:grid grid-cols-2 justify-items-center mx-auto w-full gap-8"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
          {/* Standard Card */}
          <motion.div 
            className="col-span-1"
            variants={fadeInUp}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <PriceCard
              bottomLine={"border-b border-b-mediumgray pb-5"}
              MainDiveClasses={
                "bg-lightgray text-offblack h-[700px] w-[500px] 2xl:h-[700px] 2xl:w-[500px]"
              }
              listClasses={"pl-[30px] 2xl:pl-[50px]"}
              pricingColor={basicPlan.pricingColor}
              tagging={basicPlan.tagging}
              label={basicPlan.label}
              price={basicPlan.price}
              duration={basicPlan.duration}
              planId={basicPlan.planId}
              description={basicPlan.description}
              buttonText={basicPlan.buttonText}
              listItems={basicPlan.listItems}
            />
          </motion.div>
          
          {/* Premium Card */}
          <motion.div 
            className="col-span-1"
            variants={fadeInUp}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <PriceCard
              bottomLine={"border-b border-b-mediumgray pb-5"}
              MainDiveClasses={
                "bg-lightgray text-offblack h-[700px] w-[500px] 2xl:h-[700px] 2xl:w-[500px]"
              }
              listClasses={"pl-[30px] 2xl:pl-[50px]"}
              pricingColor={proPlan.pricingColor}
              label={proPlan.label}
              price={proPlan.price}
              duration={proPlan.duration}
              planId={proPlan.planId}
              description={proPlan.description}
              buttonText={proPlan.buttonText}
              listItems={proPlan.listItems}
            />
          </motion.div>
            </motion.div>
            </div>
          </div>

        {/* Compare Plans Button — 3rem below pricing cards */}
        <motion.div
          className={`text-center ${PUBLIC_HEADING_TO_CONTENT_CLASS}`}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <span onClick={togglePackageDetails}>
          <ButtonText
            variant={"SecondaryText"}
            className="flex items-center justify-center mx-auto hover:text-primaryblue transition-colors duration-200 cursor-pointer text-sm group"
          >
            <span>Compare Plans</span>
            <motion.svg
              className="ml-2 w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              animate={{ rotate: showPackageDetails ? 180 : 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </motion.svg>
          </ButtonText>
          </span>
        </motion.div>

        {/* Package Details Table — 6rem below Compare Plans */}
        <motion.div
          ref={tableRef}
          className={showPackageDetails ? PUBLIC_CONTENT_TO_CTA_CLASS : ""}
          initial="hidden"
          animate={showPackageDetails ? "visible" : "hidden"}
          variants={{
            hidden: {
              opacity: 0,
              height: 0,
              transition: { duration: 0.3, ease: "easeInOut" },
            },
            visible: {
              opacity: 1,
              height: "auto",
              transition: { duration: 0.3, ease: "easeInOut" },
            },
          }}
          style={{ overflow: "hidden" }}
        >
          <div className="tp-container-small mx-auto">
            <PackageDetailsTable />
          </div>
        </motion.div>
        </div>
      </div>
    </>
  );
};

export default HeroImage;
